import { US_STATES_AND_TERRITORIES } from '~/constants';
import {
  MIN_LOCATION_SEARCH_CHARS,
  searchGeographies,
} from '~/features/provider-hlx/service-listings/geographyApi';
import type { GeographyOption } from '~/features/provider-hlx/service-listings/providerServiceListingForm';
import type { MsaasJobListing, MsaasListingGeography } from '~/types/hlx/msaasJobListing';
import {
  CoverageFormSchema,
  coversPlace,
  coversStates,
  type CoverageFormValues,
  type CoverageLocationOption,
  type CoverageSubmitPayload,
} from './types';

export const DEFAULT_RADIUS_MILES = '50';

export const WORK_LOCATION_NAME: Record<string, string> = {
  REMOTE: 'Remote',
  ONSITE: 'On-site',
  PARTIAL_REMOTE: 'Hybrid',
};

const placeLabel = (kind: 'ZIP' | 'CITY', city: string, state: string, zip: string) =>
  kind === 'ZIP' ? (city && state ? `${city}, ${state} ${zip}` : zip) : `${city}, ${state}`;

const toLocationOption = (geography: GeographyOption): CoverageLocationOption => ({
  value: String(geography.id),
  label: geography.label,
  kind: geography.kind,
  city: geography.city,
  state: geography.state,
  zip: geography.zip,
});

// One search per pause in typing is the picker's job; a failed lookup just shows no matches.
export const loadCoverageLocations = async (input: string): Promise<CoverageLocationOption[]> => {
  if (input.trim().length < MIN_LOCATION_SEARCH_CHARS) return [];
  try {
    return (await searchGeographies(input)).map(toLocationOption);
  } catch {
    return [];
  }
};

/** A saved coverage point shown in the picker again. */
export const geographyToLocationOption = (
  geography: MsaasListingGeography | undefined
): CoverageLocationOption | null => {
  if (!geography) return null;
  const kind = geography.preferenceType ?? 'CITY';
  const city = geography.city ?? '';
  const state = geography.stateCode ?? '';
  const zip = geography.zipCode ?? '';
  return {
    value: geography.sourceZipGeographyId != null ? String(geography.sourceZipGeographyId) : '',
    label: placeLabel(kind, city, state, zip),
    kind,
    city,
    state,
    zip,
  };
};

export const hydrateCoverageForm = (
  listing: Pick<MsaasJobListing, 'stateCodes' | 'geographies'> | undefined
): CoverageFormValues => {
  const first = listing?.geographies?.[0];
  return {
    states: (listing?.stateCodes ?? []).map(
      (code) =>
        US_STATES_AND_TERRITORIES.find((state) => state.value === code) ?? {
          value: code,
          label: code,
        }
    ),
    location: geographyToLocationOption(first),
    radiusMiles: first?.radiusMiles != null ? String(first.radiusMiles) : DEFAULT_RADIUS_MILES,
    providerIds: [],
  };
};

/** The validation that applies to the listing's work location: only what is saved is required. */
export const createCoverageFormSchema = (workLocation: string) =>
  CoverageFormSchema.superRefine((values, ctx) => {
    if (coversStates(workLocation) && values.states.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Select at least one license state',
        path: ['states'],
      });
    }
    if (!coversPlace(workLocation)) return;
    if (!values.location) {
      ctx.addIssue({ code: 'custom', message: 'Pick a city or ZIP', path: ['location'] });
    }
    const radius = Number(values.radiusMiles);
    if (!values.radiusMiles.trim() || !Number.isFinite(radius) || radius <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Distance must be greater than 0',
        path: ['radiusMiles'],
      });
    }
  });

const toGeography = (
  location: CoverageLocationOption,
  radiusMiles: number
): MsaasListingGeography => {
  const sourceId = Number(location.value);
  return {
    preferenceType: location.kind,
    city: location.city,
    stateCode: location.state,
    zipCode: location.kind === 'ZIP' ? location.zip : null,
    sourceZipGeographyId: Number.isInteger(sourceId) && location.value !== '' ? sourceId : null,
    radiusMiles,
  };
};

const sameValues = <T>(a: T[], b: T[]) =>
  a.length === b.length && [...a].sort().every((value, index) => value === [...b].sort()[index]);

/**
 * The save carries only the coverage that applies to the work location (DD16), so the server's
 * per-location validation never sees a stale input. A coverage that no longer applies but is still
 * stored is cleared by writing it empty. What matches the saved listing (and the roster as it was
 * read) is not written again, so a Next with no edits writes nothing.
 */
export const buildCoveragePayload = (
  values: CoverageFormValues,
  listing: Pick<MsaasJobListing, 'workLocation' | 'stateCodes' | 'geographies'> | undefined,
  rosterLoaded: boolean,
  loadedRosterIds?: number[]
): CoverageSubmitPayload => {
  const workLocation = listing?.workLocation ?? 'REMOTE';
  const stateCodes = coversStates(workLocation) ? values.states.map((state) => state.value) : [];
  const savedStateCodes = listing?.stateCodes ?? [];
  const location = coversPlace(workLocation) ? values.location : null;
  const radius = Number(values.radiusMiles);
  const savedGeographies = listing?.geographies ?? [];
  const sameGeography =
    location === null
      ? savedGeographies.length === 0
      : savedGeographies.length === 1 &&
        geographyToLocationOption(savedGeographies[0])?.value === location.value &&
        Number(savedGeographies[0].radiusMiles) === radius;
  return {
    stateCodes,
    persistStates: !sameValues(stateCodes, savedStateCodes),
    // Unchanged, the saved list stands in, so the listing keeps the coordinates the server resolved.
    geographies: sameGeography ? savedGeographies : location ? [toGeography(location, radius)] : [],
    persistGeographies: !sameGeography,
    providerIds: rosterLoaded ? values.providerIds : null,
    // Still reported to the listing, so the review step counts the roster.
    persistProviders: !(loadedRosterIds && sameValues(values.providerIds, loadedRosterIds)),
  };
};

/** DD16: what the owner entered that the saved work location will not keep. */
export const droppedCoverageNotes = (
  workLocation: string,
  values: Pick<CoverageFormValues, 'states' | 'location' | 'radiusMiles'>
): string[] => {
  const name = WORK_LOCATION_NAME[workLocation] ?? workLocation;
  const notes: string[] = [];
  if (!coversPlace(workLocation) && values.location) {
    notes.push(
      `Your ${values.radiusMiles || DEFAULT_RADIUS_MILES} mi from ${values.location.label} won't be saved for ${name}.`
    );
  }
  if (!coversStates(workLocation) && values.states.length > 0) {
    notes.push(
      `Your ${values.states.length} license ${values.states.length === 1 ? 'state' : 'states'} won't be saved for ${name}.`
    );
  }
  return notes;
};
